import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { ArrowLeft } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { useProductTypesApi } from '../../../../../common/hooks/use-product-types-api.hook';
import type { ProductType } from '../../../../../common/models/product-type.model';
import type { ProductFamily } from '../../../../../common/models/product-family.model';
import { APPLICATION_TYPE } from '../../../my-products/models/product.model';

import { StepIndicator } from '../setup/StepIndicator';
import { FamilyStep } from '../setup/FamilyStep';
import { ApplicationTypeStep } from '../setup/ApplicationTypeStep';
import { ProductTypeStep } from '../setup/ProductTypeStep';

// =============================================================================
// Types
// =============================================================================

export type StepId = 'family' | 'applicationTypes' | 'productType';

export interface SetupResult {
  familyId: string;
  applicationTypes?: APPLICATION_TYPE[];
  productType?: string;
}

interface CreateProductSetupDialogProps {
  open: boolean;
  families: ProductFamily[];
  familiesLoading: boolean;
  onComplete: (result: SetupResult) => void;
  onCancel?: () => void;
}

// =============================================================================
// Step labels
// =============================================================================

const STEP_LABEL_KEYS: Record<StepId, string> = {
  family: 'PAGES.NEW_PRODUCT.V3.SETUP.FAMILY',
  applicationTypes: 'PAGES.NEW_PRODUCT.V3.SETUP.APPLICATION',
  productType: 'PAGES.NEW_PRODUCT.V3.SETUP.TYPE',
};

// =============================================================================
// Helpers
// =============================================================================

function isSurfaceFamily(family: ProductFamily): boolean {
  const vc = family.visualizationConfig;
  return !!(vc?.showApplicationTypes || vc?.showProductType);
}

// =============================================================================
// Component
// =============================================================================

export const CreateProductSetupDialog: React.FC<CreateProductSetupDialogProps> = ({
  open,
  families,
  familiesLoading,
  onComplete,
  onCancel,
}) => {
  const { t } = useTranslation();

  // ---------------------------------------------------------------------------
  // State
  // ---------------------------------------------------------------------------
  const [selectedFamilyId, setSelectedFamilyId] = useState<string | null>(null);
  const [selectedApplicationTypes, setSelectedApplicationTypes] = useState<APPLICATION_TYPE[]>([]);
  const [selectedProductType, setSelectedProductType] = useState<string | null>(null);
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [allProductTypes, setAllProductTypes] = useState<ProductType[]>([]);
  const [autoCompleted, setAutoCompleted] = useState(false);

  const productTypesApi = useProductTypesApi();

  // Fetch product types on mount
  useEffect(() => {
    const fetchProductTypes = async () => {
      try {
        const types = await productTypesApi.findAllProductTypes({ activeOnly: true });
        setAllProductTypes(types);
      } catch (error) {
        console.error('Error fetching product types:', error);
      }
    };
    fetchProductTypes();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ---------------------------------------------------------------------------
  // Derived state
  // ---------------------------------------------------------------------------
  const selectedFamily = useMemo(() => {
    return families.find(f => f._id === selectedFamilyId) || null;
  }, [families, selectedFamilyId]);

  // Compute dynamic steps
  const steps = useMemo((): StepId[] => {
    const result: StepId[] = [];

    if (families.length > 1) {
      result.push('family');
    }

    if (selectedFamily && isSurfaceFamily(selectedFamily)) {
      result.push('applicationTypes');
      result.push('productType');
    } else if (!selectedFamilyId && families.length > 1) {
      result.push('applicationTypes');
      result.push('productType');
    }

    return result;
  }, [families.length, selectedFamily, selectedFamilyId]);

  const currentStepId = steps[currentStepIndex] || null;

  // Filter product types by selected application types
  const availableProductTypes = useMemo(() => {
    return allProductTypes.filter(pt =>
      pt.applicationTypes.some(appType =>
        selectedApplicationTypes.includes(appType as APPLICATION_TYPE)
      )
    );
  }, [selectedApplicationTypes, allProductTypes]);

  // ---------------------------------------------------------------------------
  // Single family auto-selection
  // ---------------------------------------------------------------------------
  useEffect(() => {
    if (autoCompleted) return;
    if (families.length !== 1) return;

    const singleFamily = families[0];
    setSelectedFamilyId(singleFamily._id);

    if (!isSurfaceFamily(singleFamily)) {
      setAutoCompleted(true);
      onComplete({ familyId: singleFamily._id });
    }
  }, [families, onComplete, autoCompleted]);

  // ---------------------------------------------------------------------------
  // Handlers
  // ---------------------------------------------------------------------------
  /**
   * Choosing a family IS the answer.
   *
   * A grid of single-choice cards followed by a Continue button asks the
   * vendor to confirm something they have already said, and the confirmation
   * carries no information — there is nothing to review and nothing to change
   * their mind about that clicking a different card would not fix. So the
   * click advances, and a non-surface family (which has no further questions)
   * finishes the setup outright.
   */
  const handleFamilySelect = useCallback((familyId: string) => {
    const family = families.find(f => f._id === familyId) || null;

    setSelectedFamilyId(familyId);
    setSelectedApplicationTypes([]);
    setSelectedProductType(null);

    if (family && !isSurfaceFamily(family)) {
      onComplete({ familyId });
      return;
    }

    setCurrentStepIndex(prev => prev + 1);
  }, [families, onComplete]);

  const handleApplicationTypeToggle = useCallback((type: APPLICATION_TYPE) => {
    setSelectedApplicationTypes(prev => {
      const newTypes = prev.includes(type)
        ? prev.filter(t => t !== type)
        : [...prev, type];
      return newTypes;
    });
    setSelectedProductType(null);
  }, []);

  const handleProductTypeSelect = useCallback((typeName: string) => {
    setSelectedProductType(typeName);
  }, []);

  const handleContinue = useCallback(() => {
    if (!currentStepId) return;

    if (currentStepId === 'family' && selectedFamily && !isSurfaceFamily(selectedFamily)) {
      onComplete({ familyId: selectedFamily._id });
      return;
    }

    if (currentStepIndex >= steps.length - 1) {
      onComplete({
        familyId: selectedFamilyId!,
        applicationTypes: selectedApplicationTypes,
        productType: selectedProductType || undefined,
      });
      return;
    }

    setCurrentStepIndex(prev => prev + 1);
  }, [
    currentStepId,
    currentStepIndex,
    steps.length,
    selectedFamily,
    selectedFamilyId,
    selectedApplicationTypes,
    selectedProductType,
    onComplete,
  ]);

  const handleBack = useCallback(() => {
    if (currentStepIndex > 0) {
      setCurrentStepIndex(prev => prev - 1);
    }
  }, [currentStepIndex]);

  // ---------------------------------------------------------------------------
  // Continue button enabled state
  // ---------------------------------------------------------------------------
  const isContinueEnabled = useMemo(() => {
    switch (currentStepId) {
      case 'family':
        return !!selectedFamilyId;
      case 'applicationTypes':
        return selectedApplicationTypes.length > 0;
      case 'productType':
        return !!selectedProductType;
      default:
        return false;
    }
  }, [currentStepId, selectedFamilyId, selectedApplicationTypes, selectedProductType]);

  const isLastStep = currentStepIndex >= steps.length - 1;

  // ---------------------------------------------------------------------------
  // Don't render if not open or no steps (auto-completed)
  // ---------------------------------------------------------------------------
  if (!open) return null;
  if (steps.length === 0) return null;

  // ---------------------------------------------------------------------------
  // Step titles and descriptions
  // ---------------------------------------------------------------------------
  const getStepTitle = (): string => {
    switch (currentStepId) {
      case 'family':
        return t('PAGES.NEW_PRODUCT.V3.SETUP.FAMILY');
      case 'applicationTypes':
        return t('PAGES.NEW_PRODUCT.V3.SETUP.APPLICATION');
      case 'productType':
        return t('PAGES.NEW_PRODUCT.V3.SETUP.TYPE');
      default:
        return t('PAGES.NEW_PRODUCT.V3.SETUP.TITLE');
    }
  };

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------
  return (
    <div className="flex min-h-[calc(100vh-140px)] flex-col">
      {/* Back button row */}
      {onCancel && (
        <div className="flex justify-start px-4 pt-2 sm:px-6">
          <Button
            variant="ghost"
            onClick={currentStepIndex > 0 ? handleBack : onCancel}
            data-testid="setup-cancel-button"
            className="h-9 text-sm text-muted-foreground hover:text-foreground"
          >
            <ArrowLeft className="rtl:rotate-180" />
            {t('COMMON.BACK')}
          </Button>
        </div>
      )}

      {/* Step indicator */}
      {steps.length > 1 && (
        <div className="mx-auto w-full max-w-[640px] px-4 pb-3 sm:px-6">
          <StepIndicator
            steps={steps}
            currentStepIndex={currentStepIndex}
            labels={Object.fromEntries(Object.entries(STEP_LABEL_KEYS).map(([k, v]) => [k, t(v)])) as Record<StepId, string>}
          />
        </div>
      )}

      {/* Main content — centered */}
      <div className="flex flex-1 flex-col items-center px-4 pb-8 sm:px-6">
        <div className="flex w-full max-w-[640px] flex-1 flex-col">
          {/* Step header */}
          <div className={cn('mb-8 text-center', steps.length > 1 && 'mt-16')}>
            <h1 className="mb-2 font-heading text-2xl font-medium tracking-tight text-foreground">
              {getStepTitle()}
            </h1>
            <p className="mx-auto max-w-[440px] text-sm leading-relaxed text-muted-foreground">
              {currentStepId === 'family' && t('PAGES.NEW_PRODUCT.V3.SETUP.FAMILY_DESCRIPTION')}
              {currentStepId === 'applicationTypes' && t('PAGES.NEW_PRODUCT.V3.SETUP.APPLICATION_DESCRIPTION')}
              {currentStepId === 'productType' && t('PAGES.NEW_PRODUCT.V3.SETUP.PRODUCT_TYPE_DESCRIPTION')}
            </p>
          </div>

          {/* Step content */}
          <div className="flex-1">
            {currentStepId === 'family' && (
              <FamilyStep
                families={families}
                selectedFamilyId={selectedFamilyId}
                onSelect={handleFamilySelect}
              />
            )}

            {currentStepId === 'applicationTypes' && (
              <ApplicationTypeStep
                selectedTypes={selectedApplicationTypes}
                onToggle={handleApplicationTypeToggle}
              />
            )}

            {currentStepId === 'productType' && (
              <ProductTypeStep
                productTypes={availableProductTypes}
                selectedType={selectedProductType}
                onSelect={handleProductTypeSelect}
              />
            )}
          </div>
        </div>
      </div>

      {/* Bottom navigation */}
      <div className="mt-auto flex justify-center border-t bg-card px-4 py-5 sm:px-6">
        <div className="flex w-full max-w-[640px] items-center justify-between">
          <div />

          {currentStepId !== 'family' && (
          <Button
            onClick={handleContinue}
            disabled={!isContinueEnabled}
            variant="outline"
            size="lg"
            data-testid="setup-continue-button"
            className="px-7"
          >
            {isLastStep ? t('PAGES.NEW_PRODUCT.V3.SETUP.GET_STARTED') : t('PAGES.NEW_PRODUCT.V3.SETUP.CONTINUE')}
          </Button>
          )}
        </div>
      </div>
    </div>
  );
};
